import { Category } from '@/services/m-commerce-api/product/categories'
import { Subcategory } from '@/services/m-commerce-api/product/subcategories'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    mobiBridge?: {
      postMessage: (message: string) => void
    }
    ReactNativeWebview?: {
      postMessage: (message: string) => void
    }
  }
}

type TrackingValue = string | number | boolean | null | undefined

export interface ShopItemParams {
  item_id: string
  item_name: string
  item_brand?: string
  price?: number
  quantity?: number
}

interface ShopEventParams {
  items?: ShopItemParams[]
  [key: string]: TrackingValue | ShopItemParams[] | undefined
}

const FLOW_NAME = 'shop'
const ENGAGED_3D_KEY = 'shop_engaged_3d'

const sanitizeParams = (
  params: Record<string, TrackingValue>
): Record<string, string | number | boolean> => {
  return Object.entries(params).reduce<Record<string, string | number | boolean>>(
    (acc, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return acc
      }

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        acc[key] = value
      }

      return acc
    },
    {}
  )
}

const getPlatform = (): 'app' | 'web' => {
  if (typeof window === 'undefined') {
    return 'web'
  }

  return window.mobiBridge != null || window.ReactNativeWebview != null
    ? 'app'
    : 'web'
}

const getDeviceType = (): 'iphone' | 'android' | 'desktop' => {
  if (typeof navigator === 'undefined') {
    return 'desktop'
  }

  const userAgent = navigator.userAgent.toLowerCase()

  if (['iphone', 'ipad', 'ipod', 'ios'].some((device) => userAgent.includes(device))) {
    return 'iphone'
  }

  if (userAgent.includes('android')) {
    return 'android'
  }

  return 'desktop'
}

const isShop3dEngaged = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return window.sessionStorage.getItem(ENGAGED_3D_KEY) === 'yes'
  } catch {
    return false
  }
}

const markShop3dEngaged = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(ENGAGED_3D_KEY, 'yes')
  } catch {
    // private mode / blocked storage
  }
}

const resolveShopBrandName = (
  subCategoryId?: number,
  subcategories?: Subcategory[],
  categoryTree?: Category[]
): string => {
  if (subCategoryId == null) {
    return 'unknown'
  }

  const fromList = subcategories?.find((item) => item.id === subCategoryId)?.name
  if (fromList != null && fromList !== '') {
    return fromList
  }

  for (const category of categoryTree ?? []) {
    const found = category.productSubCategoryDto?.find((item) => item.id === subCategoryId)
    if (found?.name != null && found.name !== '') {
      return found.name
    }
  }

  return 'unknown'
}

const buildShopItem = (params: {
  id?: string | number
  name?: string
  brand?: string
  price?: number
  quantity?: number
}): ShopItemParams => {
  return {
    item_id: String(params.id ?? 'unknown'),
    item_name: params.name != null && params.name !== '' ? params.name : 'unknown',
    item_brand: params.brand != null && params.brand !== '' ? params.brand : 'unknown',
    price: params.price,
    quantity: params.quantity ?? 1
  }
}

const trackShopEvent = (
  eventName: string,
  params: ShopEventParams = {}
): void => {
  if (typeof window === 'undefined') {
    return
  }

  const { items, ...rest } = params
  const payload: Record<string, unknown> = {
    event: eventName,
    flow_name: FLOW_NAME,
    platform: getPlatform(),
    device_type: getDeviceType(),
    engaged_3d: isShop3dEngaged() ? 'yes' : 'no',
    ...sanitizeParams(rest as Record<string, TrackingValue>)
  }

  if (items != null && items.length > 0) {
    payload.items = items
  }

  if (process.env.NODE_ENV !== 'production') {
    payload.debug_mode = true
  }

  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push(payload)

  if (process.env.NODE_ENV !== 'production') {
    console.log('[GA4][shop]', payload)
  }
}

const hasTracked3dForProduct = (productId: string | number): boolean => {
  if (typeof window === 'undefined') {
    return true
  }

  try {
    return window.sessionStorage.getItem(`shop_3d_interacted_${String(productId)}`) === '1'
  } catch {
    return false
  }
}

const markTracked3dForProduct = (productId: string | number): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(`shop_3d_interacted_${String(productId)}`, '1')
  } catch {
    // private mode / blocked storage
  }
}

const trackInteract3dModel = (params: {
  itemId: string | number
  brandName?: string
  modelName?: string
}): void => {
  if (hasTracked3dForProduct(params.itemId)) {
    markShop3dEngaged()
    return
  }

  markTracked3dForProduct(params.itemId)
  markShop3dEngaged()
  trackShopEvent('interact_3d_model', {
    item_id: String(params.itemId),
    brand_name: params.brandName,
    model_name: params.modelName,
    items: [
      buildShopItem({
        id: params.itemId,
        name: params.modelName,
        brand: params.brandName
      })
    ]
  })
}

const hasTrackedPurchase = (transactionId: string): boolean => {
  if (typeof window === 'undefined') {
    return true
  }

  try {
    return window.sessionStorage.getItem(`shop_purchase_${transactionId}`) === '1'
  } catch {
    return false
  }
}

const markTrackedPurchase = (transactionId: string): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(`shop_purchase_${transactionId}`, '1')
  } catch {
    // private mode / blocked storage
  }
}

export {
  buildShopItem,
  hasTrackedPurchase,
  markShop3dEngaged,
  markTrackedPurchase,
  resolveShopBrandName,
  trackInteract3dModel,
  trackShopEvent
}
